import { forwardRef, Fragment, useId, type ReactNode } from 'react';
import { CaretDownIcon } from '@phosphor-icons/react/dist/csr/CaretDown';
import { CaretUpIcon } from '@phosphor-icons/react/dist/csr/CaretUp';
import { useTranslation } from 'react-i18next';

import styles from './DataTable.module.css';
import { RowActionsMenu, type RowAction } from './RowActionsMenu';
import { EmptyState, type EmptyStateCopy } from './states/EmptyState';
import { ErrorState, type ErrorCopy } from './states/ErrorState';
import { LoadingSkeleton } from './states/LoadingSkeleton';
import { NoResultsState, type NoResultsCopy } from './states/NoResultsState';

/**
 * DataTable — the one table behind Cases, Requests, Questions, Justifications,
 * Devices and Users. Six bespoke tables is the failure mode being replaced.
 *
 * The four non-ready states are the point of the component, and three decisions
 * about them are worth reading before changing anything here.
 *
 * 1. **A state does not replace the table; it replaces the ROWS.** The plan
 *    returns a bare `<div>` for loading, empty, no-results and error, which
 *    throws away the caption (the list's accessible name), the column
 *    semantics and the scroll region at exactly the moment a screen-reader user
 *    is trying to work out what happened to the list they were reading. Here
 *    the `<table>`, its `<caption>` and its `<thead>` are constant and only the
 *    `<tbody>` changes.
 *
 * 2. **The polite live region is mounted in every state, empty when it has
 *    nothing to say.** A live region that is inserted into the DOM with its
 *    message already inside it has nothing for the platform to diff, and is
 *    announced unreliably or not at all. Rendering it once and changing only
 *    its text is what makes "loading -> ready" audible.
 *
 * 3. **The error announces through `role="alert"`, and the polite region goes
 *    silent.** Handoff, Accessibility: "Toasts announce via aria-live='polite';
 *    errors via assertive." Both regions carrying the same sentence would read
 *    it out twice.
 *
 * Sorting is server state: this component reads `sort` and reports intent
 * through `onSort`. It deliberately holds no sort state of its own — a table
 * that sorted its own page would show an agent a different order from the one
 * the URL encodes, and the handoff makes `sort` part of the shareable view.
 */

export type ListStatus = 'loading' | 'ready' | 'empty' | 'no-results' | 'error';

export interface Row {
  id: string;
  [key: string]: unknown;
}

export interface Column<R extends Row = Row> {
  key: string;
  /** Already translated. A primitive that owned its column names would ship
   *  English to an Arabic agent — task 20's rule, and it holds here. */
  label: string;
  sortable?: boolean;
  /**
   * Handoff, Columns: "numbers, dates and durations end-aligned with
   * tabular-nums". Implies `neverMirrors`: an end-aligned figure still has to
   * read left-to-right inside its cell.
   */
  numeric?: boolean;
  /**
   * Handoff, Arabic & RTL: "Never mirrors — wrap each in dir='ltr': phone
   * numbers, case numbers, timestamps, code identifiers." The wrapper is an
   * inline `<bdi>` rather than the cell, so the COLUMN keeps the page's
   * alignment while the VALUE keeps its own order.
   */
  neverMirrors?: boolean;
  /** Renders the cell body — a StatusPill, a bilingual city name, a link. */
  render?: (row: R) => ReactNode;
}

export interface SortState {
  field: string;
  direction: 'asc' | 'desc';
}

export interface DataTableProps {
  /** Handoff, Accessibility: "a caption naming the list." Required. */
  caption: string;
  columns: readonly Column[];
  rows?: readonly Row[];
  status: ListStatus;
  /**
   * How many rows exist behind the current filters, not how many are on this
   * page. Announced when the list becomes ready — "1284 rows" is the fact an
   * agent needs and the one a page of 25 cannot tell them.
   */
  totalCount?: number;
  sort?: SortState;
  onSort?: (field: string) => void;
  onRetry?: () => void;
  onClearFilters?: () => void;
  /** Handoff: "Row -> detail: click or Enter opens the case detail." */
  onRowActivate?: (row: Row) => void;
  rowActions?: readonly RowAction<Row>[];
  /**
   * M-18 finding M-4: the audit before/after diff used to render into the
   * rightmost cell of a table that scrolls sideways past 900px min-width —
   * exactly the wrong place for canonical JSON, hashes and SMS template
   * bodies, the values that most need to be read at a glance. Returning a
   * node here (or `null`/`undefined` for "no expansion on this row") emits a
   * full-width `<tr>` right after the row, its single `<td>` spanning every
   * column and exempt from the fixed row height — the one place in this
   * component content is allowed to set its own height.
   */
  renderExpansion?: (row: Row) => ReactNode;
  /** Handoff, State Management: `selectedId`. */
  selectedId?: string;
  /** How many skeleton rows the loading state draws. */
  skeletonRows?: number;
  empty?: EmptyStateCopy;
  /** The primary action on the empty state — "New case", and nothing else. */
  emptyAction?: ReactNode;
  noResults?: NoResultsCopy;
  error?: ErrorCopy;
  className?: string;
}

/** Handoff, Columns: "Max 8 columns by default — the rest live in a column picker." */
const MAX_COLUMNS = 8;

export const DataTable = forwardRef<HTMLDivElement, DataTableProps>(function DataTable(
  {
    caption,
    columns,
    rows = [],
    status,
    totalCount,
    sort,
    onSort,
    onRetry,
    onClearFilters,
    onRowActivate,
    rowActions,
    renderExpansion,
    selectedId,
    skeletonRows = 8,
    empty,
    emptyAction,
    noResults,
    error,
    className,
  },
  ref,
) {
  const { t } = useTranslation();
  const captionId = `${useId()}caption`;

  if (import.meta.env.DEV && columns.length > MAX_COLUMNS) {
    console.error(
      `DataTable: ${String(columns.length)} columns. The handoff caps a default view at ` +
        `${String(MAX_COLUMNS)} and puts the rest behind a column picker — a table that ` +
        'scrolls sideways past eight columns is a table nobody reads the end of.',
    );
  }

  const columnCount = columns.length + (rowActions === undefined ? 0 : 1);
  const ready = status === 'ready';

  const ariaSort = (column: Column): 'ascending' | 'descending' | 'none' | undefined => {
    // Only a sortable column gets the attribute at all: `aria-sort="none"` on a
    // column that cannot be sorted tells a screen reader it can be.
    if (column.sortable !== true) return undefined;
    if (sort?.field !== column.key) return 'none';
    return sort.direction === 'asc' ? 'ascending' : 'descending';
  };

  /**
   * The polite announcement. Empty for `ready`-with-no-count and for `error`,
   * which announces assertively through the state box instead.
   */
  const announcement = (): string => {
    switch (status) {
      case 'loading':
        return t('list.loading');
      case 'empty':
        return empty?.title ?? t('list.emptyTitle');
      case 'no-results':
        return noResults?.title ?? t('list.noResultsTitle');
      case 'error':
        return '';
      default:
        return t('table.rowCount', { count: totalCount ?? rows.length });
    }
  };

  const emptyCell = (
    <>
      {/* Handoff: "Empty cell: em dash — never a blank cell." The dash is the
          VISUAL half; on its own it is read as "dash", "em dash" or nothing at
          all depending on the screen reader, which is the blank cell again one
          layer down. The word beside it is the other half. */}
      <span className={styles['empty']} aria-hidden="true">
        —
      </span>
      <span className={styles['srOnly']}>{t('table.notRecorded')}</span>
    </>
  );

  function cellBody(column: Column, row: Row): ReactNode {
    if (column.render !== undefined) {
      const rendered = column.render(row);
      return rendered === null || rendered === undefined || rendered === '' ? emptyCell : rendered;
    }

    const raw = row[column.key];
    // Not `!raw`: that turns a real count of 0 into "not recorded".
    if (raw === null || raw === undefined || raw === '') return emptyCell;

    const text = String(raw);
    const isolated =
      column.numeric === true || column.neverMirrors === true ? <bdi dir="ltr">{text}</bdi> : text;

    // Handoff, Rows: "long text truncates ... with a tooltip". The truncation is
    // in the stylesheet; `title` is the tooltip. Only on a plain string — a
    // `title` over arbitrary JSX would announce the element's flattened text
    // across whatever the caller actually rendered.
    return (
      <span className={styles['cellText']} title={text}>
        {isolated}
      </span>
    );
  }

  function leadingCell(column: Column, row: Row): ReactNode {
    const raw = row[column.key];
    const openable =
      onRowActivate !== undefined &&
      column.render === undefined &&
      raw !== null &&
      raw !== undefined &&
      raw !== '';

    if (!openable) return cellBody(column, row);

    return (
      <button
        type="button"
        className={styles['opener']}
        onClick={() => {
          onRowActivate(row);
        }}
      >
        {cellBody(column, row)}
      </button>
    );
  }

  return (
    <div className={[styles['wrap'], className].filter(Boolean).join(' ')}>
      {/*
        Mounted in every state. See note 2 at the top of this file.
        `role="status"` carries an implicit aria-live="polite"; both are written
        so the intent survives someone changing the role.
      */}
      <div className={styles['srOnly']} role="status" aria-live="polite">
        {announcement()}
      </div>

      <div
        ref={ref}
        className={styles['scroll']}
        role="region"
        aria-labelledby={captionId}
        tabIndex={0}
      >
        <table className={styles['table']}>
          <caption id={captionId} className={styles['caption']}>
            {caption}
          </caption>

          <thead className={styles['head']}>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={[
                    styles['headCell'],
                    column.numeric === true ? styles['numeric'] : null,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  {...(ariaSort(column) === undefined ? {} : { 'aria-sort': ariaSort(column) })}
                >
                  {column.sortable === true && onSort !== undefined ? (
                    <button
                      type="button"
                      className={styles['sortBtn']}
                      // Native `disabled`, not aria-disabled: sorting a list
                      // that has no rows is genuinely unavailable, so it should
                      // leave the tab order too (task 20's split).
                      disabled={!ready}
                      onClick={() => {
                        onSort(column.key);
                      }}
                    >
                      {column.label}
                      {sort?.field === column.key ? (
                        <>
                          {/* Handoff, migration map: "Double sort arrows on
                              every column" -> "Single accent caret on the
                              sorted column only." */}
                          <span
                            className={styles['caret']}
                            aria-hidden="true"
                            data-testid="sortCaret"
                          >
                            {sort.direction === 'asc' ? (
                              <CaretUpIcon size={12} weight="fill" />
                            ) : (
                              <CaretDownIcon size={12} weight="fill" />
                            )}
                          </span>
                          <span className={styles['srOnly']}>
                            {sort.direction === 'asc'
                              ? t('table.sortedAscending')
                              : t('table.sortedDescending')}
                          </span>
                        </>
                      ) : null}
                    </button>
                  ) : (
                    column.label
                  )}
                </th>
              ))}

              {rowActions === undefined ? null : (
                <th scope="col" className={[styles['headCell'], styles['actionsHead']].join(' ')}>
                  <span className={styles['srOnly']}>{t('table.rowActions')}</span>
                </th>
              )}
            </tr>
          </thead>

          {status === 'loading' ? (
            <LoadingSkeleton columns={columnCount} rows={skeletonRows} />
          ) : null}

          {ready ? (
            <tbody className={styles['body']}>
              {rows.map((row) => {
                const expansion = renderExpansion?.(row);
                const expands = expansion !== null && expansion !== undefined;

                return (
                  <Fragment key={row.id}>
                    <tr
                      className={styles['row']}
                      // `aria-current` is a global ARIA state, so it is valid on a
                      // <tr>. `aria-selected` is not: outside a grid, a row has no
                      // selection state to expose.
                      {...(row.id === selectedId ? { 'aria-current': true as const } : {})}
                    >
                      {columns.map((column, index) => (
                        <td
                          key={column.key}
                          className={[
                            styles['cell'],
                            column.numeric === true ? styles['numeric'] : null,
                            index === 0 ? styles['cellLead'] : null,
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          {index === 0 ? leadingCell(column, row) : cellBody(column, row)}
                        </td>
                      ))}

                      {rowActions === undefined ? null : (
                        <td className={[styles['cell'], styles['actionsCell']].join(' ')}>
                          <RowActionsMenu
                            actions={rowActions}
                            row={row}
                            rowLabel={rowLabelOf(columns, row)}
                          />
                        </td>
                      )}
                    </tr>

                    {expands ? (
                      <tr className={styles['expansionRow']}>
                        {/*
                          `columnCount`, never `columns.length` alone: with
                          `rowActions` present the rendered header carries one
                          more `<th>` than `columns`, and a `colSpan` that
                          forgot the actions column would fall one column
                          short of the header it is supposed to match.
                        */}
                        <td className={styles['expansionCell']} colSpan={columnCount}>
                          {expansion}
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          ) : null}

          {status === 'empty' || status === 'no-results' || status === 'error' ? (
            <tbody className={styles['body']}>
              <tr>
                <td className={styles['stateCell']} colSpan={columnCount}>
                  {status === 'empty' ? (
                    <EmptyState
                      {...empty}
                      {...(emptyAction === undefined ? {} : { action: emptyAction })}
                    />
                  ) : null}
                  {status === 'no-results' ? (
                    <NoResultsState
                      {...noResults}
                      {...(onClearFilters === undefined ? {} : { onClearFilters })}
                    />
                  ) : null}
                  {status === 'error' ? (
                    <ErrorState {...error} {...(onRetry === undefined ? {} : { onRetry })} />
                  ) : null}
                </td>
              </tr>
            </tbody>
          ) : null}
        </table>
      </div>
    </div>
  );
});

/**
 * The row's own name, for the actions trigger. Six identical "Row actions"
 * buttons in a screen reader's element list name nothing at all.
 */
function rowLabelOf(columns: readonly Column[], row: Row): string | null {
  const leading = columns[0];
  if (leading === undefined) return null;
  const raw = row[leading.key];
  if (raw === null || raw === undefined || raw === '') return null;
  return String(raw);
}
